import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, type PropsWithChildren } from "react";
import { LiveRegion } from "@/shared/ui/LiveRegion";
import { ToastProvider } from "@/shared/ui/Toast";
import { useBindA11y } from "@/shared/hooks/useA11y";
import { useBindTheme } from "@/shared/hooks/useTheme";
import { OfflineIndicator } from "@/widgets/OfflineIndicator";

/**
 * App-wide providers. Each one is colocated here so the order is obvious
 * and so the root composition stays readable.
 *
 * Key choices:
 *   * QueryClient is created once per mount (defaults tuned for a control
 *     tower — short stale times, retries with backoff, no refetch on focus
 *     of *infinite* queries).
 *   * Theme is applied via a side-effect hook so the very first paint
 *     respects the persisted choice without a flicker.
 */
export function AppProviders({ children }: PropsWithChildren) {
  // These two depend only on stores + the document, so they can live
  // outside the Router. `useGlobalHotkeys` does need the router and is
  // mounted inside <AppShell> instead.
  useBindTheme();
  useBindA11y();

  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            gcTime: 5 * 60_000,
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              const status = (error as { status?: number } | null)?.status;
              if (status && status >= 400 && status < 500) return false;
              return failureCount < 2;
            },
          },
          mutations: { retry: 0 },
        },
      }),
  );

  return (
    <QueryClientProvider client={client}>
      <ToastProvider>
        {children}
        {/* Single source of truth for screen-reader announcements. */}
        <LiveRegion />
        {/* App-wide network status; renders only while offline. */}
        <OfflineIndicator />
      </ToastProvider>
    </QueryClientProvider>
  );
}
